import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "font-lexend inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 min-h-[44px] normal-case cursor-pointer active:scale-[0.97]",
  {
    variants: {
      variant: {
        // CTA Principal Verde (Auli Teal)
        default: "bg-auli-teal text-auli-navy hover:bg-auli-teal/90 shadow-sm hover:shadow-[0_8px_32px_rgba(115,216,178,0.3)] hover:scale-105",
        
        // CTA Secundário Azul Claro
        secondary: "bg-auli-blue-light text-white hover:bg-auli-blue-light/90 shadow-sm hover:shadow-[0_8px_32px_rgba(67,65,234,0.3)] hover:scale-105",
        
        // Outline com borda azul
        outline: "border-2 border-auli-blue-light/30 hover:border-auli-blue-light/60 bg-white/90 hover:bg-white text-auli-blue-light hover:shadow-lg hover:scale-105",
        
        // Accent Amarelo
        accent: "bg-auli-yellow text-auli-navy hover:bg-auli-yellow/90 shadow-sm hover:shadow-[0_8px_32px_rgba(251,222,104,0.3)] hover:scale-105",
        
        // Destrutivo Rosa Escuro
        destructive: "bg-auli-pink-dark text-white hover:bg-auli-pink-dark/90 shadow-sm hover:shadow-[0_8px_32px_rgba(252,66,93,0.3)]",
        
        // Ghost
        ghost: "hover:bg-muted hover:text-foreground text-foreground",
        
        // Link
        link: "text-auli-blue-light underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-6 py-3",
        sm: "h-10 rounded-xl px-4 text-xs min-h-[40px]", // Increased for touch targets
        lg: "h-14 rounded-2xl px-8 text-base",
        icon: "h-11 w-11 min-h-[44px] min-w-[44px]", // Ensure 44px touch target
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp 
        className={cn(buttonVariants({ variant, size, className }))} 
        ref={ref} 
        disabled={disabled || loading}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
